import { useEffect } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Link } from 'react-router-dom';
import { useTranslate } from 'react-translate.ts';
import { ObjectSchema, StringSchema } from 'yup';
import { Button, ButtonVariants } from '@tcc-online/common-components';

import { FORGOTTEN_PASSWORD_PATH } from '../../../constants/route.constants';
import AuthSelectors from '../../../selectors/AuthSelectors';
import { FormField, FormInput } from '@tcc-online/input-components';
import { TRANSLATIONS_ROOT_KEY } from '../../../constants/translation.constants';
import AuthForm from '../../../pages/Auth/AuthForm/AuthForm';
import AuthControls from '../../../pages/Auth/AuthControls/AuthControls';
import { IAuthUser } from '../../../types';

const USER_FIELD = 'username';
const PASS_FIELD = 'password';

type Values = {
    [USER_FIELD]: string;
    [PASS_FIELD]: string;
};

const initialValues: IAuthUser = {
    username: '',
    password: '',
};

type SignInFormProps = {
    onSubmit: (user: IAuthUser) => void;
};

export default function SignInForm(props: SignInFormProps) {
    const { onSubmit } = props;
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY + '.login');

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={submitForm}
            validationSchema={createValidationSchema(translate('error'))}
        >
            <InnerForm translate={translate} />
        </Formik>
    );

    function submitForm(user: IAuthUser): void {
        onSubmit(user);
    }
}

function createValidationSchema(message: string) {
    return new ObjectSchema({
        [USER_FIELD]: new StringSchema().required(''),
        [PASS_FIELD]: new StringSchema().required(message),
    });
}

function InnerForm(props: { translate: (key: string) => string }) {
    const { translate } = props;
    const { error } = AuthSelectors.useGetLogin();
    const translateError = useTranslate();
    const errorMessage = translateError(error);

    const formik = useFormikContext<Values>();
    useEffect(() => {
        if (error) {
            formik.setErrors({
                username: errorMessage,
                password: errorMessage,
            });
        }
    }, [formik, error, errorMessage]);

    return (
        <Form>
            <AuthForm>
                <FormField>
                    <FormInput
                        name={USER_FIELD}
                        label={translate('email')}
                        showErrorText={false}
                    />
                </FormField>
                <FormField>
                    <FormInput
                        type="password"
                        name={PASS_FIELD}
                        label={translate('password')}
                        autoComplete="off"
                    />
                </FormField>
            </AuthForm>
            <AuthControls>
                <Link
                    to={FORGOTTEN_PASSWORD_PATH}
                    className="primary"
                    data-test="reset-link"
                >
                    <strong>{translate('forgotten-password')}</strong>
                </Link>
                <Button
                    type="submit"
                    variant={ButtonVariants.positive}
                    data-test="submit"
                >
                    {translate('login')}
                </Button>
            </AuthControls>
        </Form>
    );
}
